/*
 * SoMatcha slideshow — section-scoped web component.
 *
 * Deliberately independent of the shared global.js SlideshowComponent/SliderComponent (which are
 * shared with announcement-bar and main-product and throw a TypeError when arrow markup is absent).
 * Handles: dot building + navigation, active-dot sync, auto-rotate with pause on hover/focus, and
 * Theme-Editor block selection.
 *
 * Slides are theme blocks (blocks/sm-slide.liquid) emitted by {% content_for 'blocks' %}, so the
 * section cannot count them in Liquid. The dots are therefore BUILT HERE from the slides actually
 * present in the DOM, and their labels come from data-label-* attributes so all copy stays in
 * locales/*.json rather than being hardcoded in JS.
 *
 * Direction-agnostic: all scrolling and active-slide detection are computed from rendered
 * getBoundingClientRect() geometry, so RTL and LTR both work without any dir multiplier — each
 * slide is exactly the viewport width, so aligning left edges brings the target fully into view
 * in either direction.
 */
if (!customElements.get('sm-slideshow')) {
  class SmSlideshow extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
      this.rafId = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    }

    connectedCallback() {
      // Queried here, not in the constructor: in the Theme Editor the element is re-created on
      // section re-render, and children must already be parsed.
      this.slider = this.querySelector('[id^="Slider-"]');
      this.slides = Array.from(this.querySelectorAll('.sm-slide'));
      this.dotsContainer = this.querySelector('.sm-slideshow__dots');
      this.autoplayEnabled = this.getAttribute('data-autoplay') === 'true';
      this.speed = (parseFloat(this.getAttribute('data-speed')) || 5) * 1000;

      if (!this.slider || this.slides.length === 0) return;

      this.labelSlides();
      this.dots = this.buildDots();

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          this.goTo(index);
          this.restart();
        });
      });

      this.slider.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      this.syncActive(0);

      if (this.autoplayEnabled && this.slides.length > 1) {
        // Pause on hover/focus, resume on leave/blur (Origin's behaviour).
        this.addEventListener('mouseenter', this.stop.bind(this));
        this.addEventListener('mouseleave', this.start.bind(this));
        this.addEventListener('focusin', this.stop.bind(this));
        this.addEventListener('focusout', this.start.bind(this));
        this.start();
      }

      // Theme Editor: scroll to the slide being edited, and pause while selected. event.target may
      // be a nested child block (heading/subheading/buttons), so match on containment too.
      this.addEventListener('shopify:block:select', (event) => {
        this.stop();
        const index = this.slides.findIndex((slide) => slide === event.target || slide.contains(event.target));
        if (index >= 0) this.goTo(index);
      });
      this.addEventListener('shopify:block:deselect', this.start.bind(this));
    }

    disconnectedCallback() {
      this.stop();
    }

    /* "1 of 5" announcements for each slide — the section can no longer emit these in Liquid. */
    labelSlides() {
      const ofLabel = this.getAttribute('data-label-of') || 'of';
      const total = this.slides.length;
      this.slides.forEach((slide, index) => {
        slide.setAttribute('aria-label', `${index + 1} ${ofLabel} ${total}`);
      });
    }

    buildDots() {
      if (!this.dotsContainer) return [];
      this.dotsContainer.textContent = '';
      // A single slide needs no pagination; the :empty CSS rule hides the container.
      if (this.slides.length < 2) return [];

      const loadLabel = this.getAttribute('data-label-load') || 'Load slide';
      const ofLabel = this.getAttribute('data-label-of') || 'of';
      const total = this.slides.length;
      const sliderId = this.slider.id;

      return this.slides.map((slide, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'slider-counter__link';
        button.setAttribute('aria-label', `${loadLabel} ${index + 1} ${ofLabel} ${total}`);
        button.setAttribute('aria-controls', sliderId);
        if (index === 0) button.setAttribute('aria-current', 'true');

        const dot = document.createElement('span');
        dot.className = 'dot';
        button.appendChild(dot);
        this.dotsContainer.appendChild(button);
        return button;
      });
    }

    behavior() {
      return this.reducedMotion.matches ? 'auto' : 'smooth';
    }

    goTo(index) {
      this.currentIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      const sliderRect = this.slider.getBoundingClientRect();
      const slideRect = this.slides[this.currentIndex].getBoundingClientRect();
      // Physical delta to align this slide's start edge with the viewport — correct in LTR and RTL
      // because each slide is exactly the viewport width.
      this.slider.scrollBy({ left: slideRect.left - sliderRect.left, behavior: this.behavior() });
      this.syncActive(this.currentIndex);
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        const sliderLeft = this.slider.getBoundingClientRect().left;
        let closest = 0;
        let smallest = Infinity;
        this.slides.forEach((slide, index) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - sliderLeft);
          if (distance < smallest) {
            smallest = distance;
            closest = index;
          }
        });
        this.syncActive(closest);
      });
    }

    syncActive(index) {
      this.currentIndex = index;
      this.dots.forEach((dot, position) => {
        const active = position === index;
        dot.classList.toggle('slider-counter__link--active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    start() {
      if (!this.autoplayEnabled || this.slides.length < 2 || this.reducedMotion.matches) return;
      this.stop();
      this.slider.setAttribute('aria-live', 'off');
      this.timer = setInterval(() => {
        this.goTo((this.currentIndex + 1) % this.slides.length);
      }, this.speed);
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
      if (this.slider) this.slider.setAttribute('aria-live', 'polite');
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  customElements.define('sm-slideshow', SmSlideshow);
}
